import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, ArrowRight, Sparkles } from "lucide-react";
import { getKbArticle } from "../data/knowledgeBase";
import { getSeoPage, SITE } from "../data/seoPages";

export default function KnowledgeArticlePage() {
  const { slug } = useParams();
  const article = getKbArticle(slug);
  if (!article) return <Navigate to="/knowledge" replace />;

  const url = `${SITE}/knowledge/${article.slug}`;
  const related = article.rel.map(getKbArticle).filter(Boolean);
  const software = article.sw.map(getSeoPage).filter(Boolean);
  const fullAnswer = article.answer.join(" ");

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Knowledge Base", item: `${SITE}/knowledge` },
          { "@type": "ListItem", position: 3, name: article.q, item: url },
        ],
      },
      {
        "@type": "QAPage",
        mainEntity: {
          "@type": "Question",
          name: article.q,
          text: article.q,
          answerCount: 1,
          acceptedAnswer: {
            "@type": "Answer",
            text: fullAnswer,
            author: { "@type": "Organization", name: "BrandBorn", url: SITE },
          },
        },
      },
      {
        "@type": "Article",
        headline: article.q,
        description: article.answer[0],
        url,
        author: { "@type": "Organization", name: "BrandBorn", url: SITE },
        publisher: { "@type": "Organization", name: "BrandBorn", url: SITE },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="knowledge-article-page">
      <Helmet>
        <title>{`${article.q} | BrandBorn Knowledge Base`}</title>
        <meta name="description" content={article.answer[0].slice(0, 158)} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={article.q} />
        <meta property="og:description" content={article.answer[0].slice(0, 158)} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <article className="px-4 py-12 max-w-3xl mx-auto">
        <nav aria-label="Breadcrumb" className="text-xs text-gray-400 mb-5 font-semibold">
          <Link to="/" className="hover:text-red-500">Home</Link> / <Link to="/knowledge" className="hover:text-red-500">Knowledge Base</Link> / <span className="text-gray-600">{article.cluster}</span>
        </nav>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-3 leading-tight">{article.q}</h1>
        <p className="text-[11px] text-gray-400 font-bold mb-5" data-testid="kb-last-updated">Last updated: June 2026 • By BrandBorn marketplace experts</p>

        {/* Quick answer for AI/featured snippets */}
        <div className="bg-gray-950 text-white rounded-2xl p-6 mb-8 border border-gray-800" data-testid="kb-quick-answer">
          <div className="flex items-center gap-2 text-xs font-bold text-blue-400 uppercase tracking-widest mb-2"><Sparkles size={13} /> Quick Answer</div>
          <p className="text-gray-200 text-sm leading-relaxed">{article.answer[0]}</p>
        </div>

        {article.answer.map((p, i) => (
          <p key={i} className="text-gray-600 leading-relaxed mb-5 text-lg">{p}</p>
        ))}

        {/* Fact block */}
        <div className="bg-slate-50 rounded-2xl p-6 border border-gray-100 my-8" data-testid="kb-fact-block">
          <h2 className="text-lg font-black text-gray-900 mb-4">Key Facts</h2>
          <ul className="space-y-2">
            {article.facts.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700 font-medium">
                <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Source / entity */}
        <p className="text-xs text-gray-400 border-l-2 border-red-500 pl-3 mb-10">
          Answered by <strong className="text-gray-600">BrandBorn</strong> — India's #1 brand onboarding agency, Official Myntra OMS Partner, builder of EcomCatalog.ai (India's first AI auto listings software). Featured in Times of India & Economic Times.
        </p>

        {/* Related */}
        {related.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-black text-gray-900 mb-4">Related Questions</h2>
            <div className="space-y-2">
              {related.map((r) => (
                <Link key={r.slug} to={`/knowledge/${r.slug}`} className="flex items-center justify-between gap-2 p-4 bg-slate-50 hover:bg-red-50 border border-gray-100 hover:border-red-200 rounded-xl font-bold text-sm text-gray-800 transition-colors">
                  {r.q} <ArrowRight size={15} className="text-red-500 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {software.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-black text-gray-900 mb-4">Related Software</h2>
            <div className="flex flex-wrap gap-2">
              {software.map((s) => (
                <Link key={s.slug} to={`/software/${s.slug}`} className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-100 rounded-full text-xs font-bold hover:bg-blue-100 transition-colors">
                  {s.kw}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-2xl p-8 text-center">
          <h2 className="text-xl font-black text-white mb-3">Need Expert Help?</h2>
          <p className="text-white/90 text-sm mb-5">Talk to BrandBorn — 1000+ brands launched, 98% approval rate, 14-day standard.</p>
          <a href="tel:+919464645491" className="inline-block px-7 py-3.5 bg-white text-red-600 rounded-xl font-black text-sm uppercase tracking-wider hover:-translate-y-0.5 transition-transform shadow-lg">Call +91-9464645491</a>
        </div>
      </article>
    </div>
  );
}
